<script lang="ts">
  import { gameStore } from '../../stores/gameStore';
  import { socketService } from '../../services/socketService';
  import { announceToScreenReader } from '../../lib/accessibility';

  $: gameState = $gameStore.gameState;
  $: scores = gameState?.scores || [];
  $: sortedScores = [...scores].sort((a, b) => b.score - a.score);

  // Determine winner(s)
  $: maxScore = sortedScores[0]?.score || 0;
  $: winners = sortedScores.filter((s) => s.score === maxScore);
  $: isTie = winners.length > 1;

  let isRestarting = false;

  // Announce game end
  $: if (gameState?.phase === 'game_end') {
    if (isTie) {
      const winnerNames = winners.map((w) => w.username).join(' and ');
      announceToScreenReader(
        `Game over! It's a tie between ${winnerNames} with ${maxScore} points!`,
        'assertive'
      );
    } else if (winners[0]) {
      announceToScreenReader(
        `Game over! ${winners[0].username} wins with ${maxScore} points!`,
        'assertive'
      );
    }
  }

  function playAgain() {
    if (isRestarting) return;
    isRestarting = true;

    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('game:playAgain');
      announceToScreenReader('Restarting game', 'assertive');
    }
  }

  function returnToLobby() {
    // Reload page to return to lobby
    window.location.reload();
  }
</script>

<div class="game-end">
  <div class="game-end-card">
    <h2>Game Over!</h2>

    <div class="winner-announcement">
      {#if isTie}
        <h3>It's a Tie!</h3>
        <p class="winners">
          {winners.map((w) => w.username).join(' and ')} tied with {maxScore} points!
        </p>
      {:else if winners[0]}
        <h3>🏆 Winner 🏆</h3>
        <p class="winner-name">{winners[0].username}</p>
        <p class="winner-score">{maxScore} points</p>
      {/if}
    </div>

    <div class="final-scoreboard">
      <h3>Final Scores</h3>
      <table>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Player</th>
            <th>Score</th>
          </tr>
        </thead>
        <tbody>
          {#each sortedScores as player, index (player.playerId)}
            <tr class:winner={player.score === maxScore}>
              <td class="rank">#{index + 1}</td>
              <td class="player">{player.username}</td>
              <td class="score">{player.score}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="actions">
      <button class="play-again-button" on:click={playAgain} disabled={isRestarting}>
        {isRestarting ? 'Restarting...' : 'Play Again'}
      </button>
      <button class="lobby-button" on:click={returnToLobby}>
        Return to Lobby
      </button>
    </div>
  </div>
</div>

<style>
  .game-end {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 2rem;
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  }

  .game-end-card {
    background: white;
    border-radius: 1rem;
    padding: 3rem;
    max-width: 800px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  h2 {
    margin: 0 0 2rem 0;
    font-size: 3rem;
    font-weight: 700;
    color: #1f2937;
    text-align: center;
  }

  .winner-announcement {
    text-align: center;
    padding: 2rem;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-radius: 0.75rem;
    margin-bottom: 2rem;
  }

  .winner-announcement h3 {
    margin: 0 0 1rem 0;
    font-size: 2rem;
    color: #92400e;
  }

  .winner-name {
    font-size: 2.5rem;
    font-weight: 700;
    color: #92400e;
    margin: 0;
  }

  .winner-score {
    font-size: 1.5rem;
    font-weight: 600;
    color: #b45309;
    margin: 0.5rem 0 0 0;
  }

  .winners {
    font-size: 1.5rem;
    font-weight: 700;
    color: #92400e;
    margin: 0;
  }

  .final-scoreboard h3 {
    margin: 0 0 1rem 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 2rem;
  }

  th {
    text-align: left;
    padding: 0.75rem;
    background: #f3f4f6;
    font-weight: 600;
    color: #4b5563;
    border-bottom: 2px solid #e5e7eb;
  }

  td {
    padding: 1rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
  }

  tr.winner {
    background: #fef3c7;
    font-weight: 700;
  }

  .rank {
    color: #6b7280;
    font-weight: 700;
  }

  .score {
    font-size: 1.25rem;
    font-weight: 700;
    color: #3b82f6;
  }

  .actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }

  button {
    padding: 1rem 2rem;
    font-size: 1.125rem;
    font-weight: 600;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .play-again-button {
    background: #3b82f6;
    color: white;
  }

  .play-again-button:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-2px);
  }

  .play-again-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .lobby-button {
    background: #6b7280;
    color: white;
  }

  .lobby-button:hover {
    background: #4b5563;
    transform: translateY(-2px);
  }
</style>
